import React, { FunctionComponent } from 'react';
import { Checkbox, FormControl, FormControlLabel, InputLabel, MenuItem, Select } from '@mui/material';

import { ColorByOption, DistanceFormat, ViewOptionSettings } from '@paragon/contract-library';

import { MemberLabelOptionsSelection } from '../controls/MemberLabelOptionsSelection';
import { PlateDetailSettingsComponent } from '../controls/PlateDetailSettingsComponent';
import { SnapSettingsSelect } from '../controls/SnapSettingsSelect';
import { COLOR_BY_LABELS, DEFAULT_COLOR_BY_OPTIONS } from '../settings';
import { SettingsDialogColumn } from '../SettingsDialogContainer';
import { SettingsControlProps } from '../types';

type Props = SettingsControlProps<ViewOptionSettings> & {
  /**
   * If set, the visible options for "Color By" will be limited to these. Defaults to `DEFAULT_COLOR_BY_OPTIONS`.
   */
  enabledColorByOptions?: ColorByOption[];
};

export const ViewOptionSettingsColumn: FunctionComponent<Props> = ({ enabledColorByOptions, value, onChange }) => {
  const colorByOptions = enabledColorByOptions ?? DEFAULT_COLOR_BY_OPTIONS;

  return (
    <SettingsDialogColumn title="View Options">
      <FormControl margin="normal" variant="standard">
        <InputLabel>Color By</InputLabel>
        <Select
          value={value.colorBy}
          onChange={(event) => onChange({ colorBy: event.target.value as ColorByOption })}
          label="Color By"
        >
          {colorByOptions.map((key) => (
            <MenuItem key={key} value={key}>
              {COLOR_BY_LABELS[key]}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <SnapSettingsSelect value={value.snapSettings} onChange={(snapSettings) => onChange({ snapSettings })} />

      <MemberLabelOptionsSelection
        memberLabelOptions={value.memberLabelOptions}
        onChange={(memberLabelOptions) => onChange({ memberLabelOptions })}
      />

      <FormControlLabel
        control={
          <Checkbox
            color="default"
            checked={value.showPlates}
            onChange={(event) => onChange({ showPlates: event.target.checked })}
          />
        }
        label="Show Plates"
      />

      <FormControlLabel
        control={
          <Checkbox
            color="default"
            disabled={!value.showPlates}
            checked={value.showPlateNames}
            onChange={(event) => onChange({ showPlateNames: event.target.checked })}
          />
        }
        label="Show Plate Names"
      />

      <PlateDetailSettingsComponent
        value={value.plateDetailSettings}
        onChange={(plateDetailSettings) => onChange({ plateDetailSettings })}
        disabled={!value.showPlates}
      />

      <FormControl margin="normal" variant="standard">
        <InputLabel>Distance Format</InputLabel>
        <Select
          value={value.distanceFormat}
          onChange={(event) => onChange({ distanceFormat: event.target.value as DistanceFormat })}
          autoWidth
          label="Distance Format"
        >
          <MenuItem value="Architectural">Architectural</MenuItem>
          <MenuItem value="FeetInchesSixteenths">Feet-Inches-Sixteenths</MenuItem>
        </Select>
      </FormControl>

      <FormControlLabel
        control={
          <Checkbox
            color="default"
            checked={value.highVisibilityMode}
            onChange={(event) => onChange({ highVisibilityMode: event.target.checked })}
          />
        }
        label="High Visibility Mode"
      />
    </SettingsDialogColumn>
  );
};
